// The zero amount inside a currency string: the `0,00` of `0,00 €`, the `0.00` of `$0.00`, or the
// bare `0` a JPY currency string carries. Not global - only the first match is an amount.
const PRICE_PLACEHOLDER = /0[.,]00|0/;

// Every position a thousands separator goes into, e.g. `1299` -> `1,299`. `\B` never matches after
// a leading `-`, so a negative amount keeps its sign unseparated.
const THOUSANDS_BOUNDARY = /\B(?=(\d{3})+(?!\d))/g;

/**
 * The cents separator the server used when it built `formatted_string`.
 *
 * EU currencies come back as `0,00 €`, everything else as `$0.00`, so reading the separator off the
 * currency string is what keeps an amount rendered here on the same notation as a price the server
 * already formatted (`formatted_price` and friends).
 */
const getDecimalSeparator = (formattedString) => (formattedString?.includes("0,00") ? "," : ".");

/**
 * An amount split into the pieces a price block renders separately - the whole part and the cents.
 *
 * The whole part carries the thousands separator that belongs to the notation: EU prices are written
 * `1299,50` with no grouping and everything else `1,299.50`, the same as the server writes them.
 */
const splitAmount = (amount, decimalSeparator) => {
  const value = Number(amount);
  const [whole, cents] = (Number.isFinite(value) ? value : 0).toFixed(2).split(".");

  return {
    whole: decimalSeparator === "," ? whole : whole.replace(THOUSANDS_BOUNDARY, ","),
    cents,
  };
};

/**
 * An amount written in the notation of the given currency string, e.g. `1299,50` beside `0,00 €`.
 *
 * Only the display is affected - the amounts posted to the CRM are the raw numbers from the pack.
 */
const formatAmount = (amount, formattedString) => {
  const decimalSeparator = getDecimalSeparator(formattedString);
  const { whole, cents } = splitAmount(amount, decimalSeparator);

  return `${whole}${decimalSeparator}${cents}`;
};

/** An amount written into its currency string, e.g. `1299,50 €`. */
const formatPrice = (amount, formattedString) =>
  formattedString
    ? formattedString.replace(PRICE_PLACEHOLDER, formatAmount(amount, formattedString))
    : formatAmount(amount, formattedString);

export { PRICE_PLACEHOLDER, getDecimalSeparator, splitAmount, formatAmount, formatPrice };
